/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Lock, AlertCircle, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import ZoneProLogo from '../components/ZoneProLogo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const signedIn = await login(email, password);
      navigate(signedIn.must_change_password ? '/choose-password' : '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || JSON.stringify(detail));
      } else {
        setError(detail || t('auth.loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-zonepro-primary">
          <div className="flex items-center justify-between">
            <Link to="/">
              <ZoneProLogo variant="white" size="lg" />
            </Link>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors"
              data-testid="language-toggle-login"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>
          </div>
          
          <div>
            <h2 className="font-bold text-4xl text-white mb-4">
              {language === 'fr' ? (
                <>Analyse de zonage<br /><span className="text-zonepro-highlight">intelligente</span></>
              ) : (
                <>Intelligent<br /><span className="text-zonepro-highlight">zoning analysis</span></>
              )}
            </h2>
            <p className="text-xl text-white/80 italic mb-4">
              {language === 'fr' ? "Votre terrain parle. On le traduit." : "Your land speaks. We translate it."}
            </p>
            <p className="text-white/60 max-w-md">
              {language === 'fr' 
                ? "Estimez rapidement le potentiel de développement d'un terrain à Montréal grâce à notre moteur d'analyse réglementaire."
                : "Quickly estimate the development potential of a lot in Montreal with our regulatory analysis engine."
              }
            </p>
          </div>

          <p className="text-white/40 text-sm">
            {t('footer.copyright')}
          </p>
        </div>

        {/* Right side - Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <Card className="w-full max-w-md border border-border/50 shadow-xl">
            <CardHeader className="text-center">
              <div className="lg:hidden flex justify-center mb-4">
                <Link to="/">
                  <ZoneProLogo variant="color" size="md" />
                </Link>
              </div>
              <div className="lg:hidden flex justify-center mb-2">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">{language === 'fr' ? 'EN' : 'FR'}</span>
                </button>
              </div>
              <CardTitle className="font-bold text-2xl text-zonepro-primary">
                {language === 'fr' ? 'Connexion Admin' : 'Admin Login'}
              </CardTitle>
              <CardDescription>
                {language === 'fr' ? 'Accès réservé aux comptes autorisés' : 'Access reserved for authorized accounts'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="login-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={language === 'fr' ? "votre@email.com" : "your@email.com"}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-zonepro-accent hover:bg-zonepro-primary text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? t('common.loading') : t('auth.loginButton')}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  <Link to="/" className="text-zonepro-accent hover:text-zonepro-primary hover:underline font-medium">
                    {language === 'fr' ? '← Retour à l\'accueil' : '← Back to home'}
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Footer on mobile */}
      <footer className="lg:hidden w-full py-4 text-center border-t border-border/40 bg-zonepro-surface/50">
        <p className="text-xs text-muted-foreground">
          {t('footer.copyright')}
        </p>
      </footer>
    </div>
  );
};

export default Login;
